import { TableRow, TableCell, Typography, Box, ClickAwayListener } from "@material-ui/core";
import { FC, useEffect, useState } from "react";
import { RenderFalconCell } from "./RenderFalconCell";
import { useStyles } from "./styles";
import { useTypeSelectors } from "hooks/useTypeSelectors";
import {
  FalconDataLists,
  FalconPlanData,
  FalconTableRow,
  FalconStandardData,
  DateLabelType,
} from "types/Falcon";
import CustomIconButton from "componentsNew/buttons/CustomIconButton";
import Edit from "@material-ui/icons/EditOutlined";
import Check from "@material-ui/icons/Check";
import Close from "@material-ui/icons/Close";
import moment from "moment";
import { falconEditPlan } from "actions/falconAction";
import { useDispatch } from "react-redux";

interface FalconRowProps {
  tableRow: FalconTableRow;
  headColumns: DateLabelType[];
  isLastCollapseRow?: boolean;
  isCollapseRowActive?: boolean;
}

export const FalconRow: FC<FalconRowProps> = ({
  tableRow,
  headColumns,
  isLastCollapseRow,
  isCollapseRowActive,
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { falconData, falconFilters } = useTypeSelectors((state) => state.falconTable);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [typePlanList, setTypePlanList] = useState<FalconPlanData[]>(
    falconData.planList.filter((item) => item.type === tableRow.id),
  );

  useEffect(() => {
    if (!typePlanList.length && ["large-van", "box-truck"].includes(tableRow.id)) {
      setTypePlanList(
        headColumns.map((item) => ({
          number_of_routes: 0,
          type: tableRow.id,
          report_date: item.date,
        })),
      );
    }
  }, [headColumns, falconData.planList, typePlanList.length, tableRow.id]);

  useEffect(() => {
    setTypePlanList(falconData.planList.filter((item) => item.type === tableRow.id));
  }, [falconData.planList, falconFilters.date.week, tableRow.id]);

  const dateType =
    falconFilters.selectType === "week" ? "report__report_date" : "report__week_number";
  const planDateType = falconFilters.selectType === "week" ? "report_date" : "week_number";

  const weekDate = falconFilters.date.week.split("-W");
  const editCondition =
    Number(weekDate[1]) >= +moment().format("W") &&
    Number(weekDate[0]) === +moment().format("yyyy");

  const applyChanges = () => {
    setEditMode(false);
    dispatch(falconEditPlan(tableRow.id, typePlanList));
  };

  const disregardChanges = () => {
    setEditMode(false);
    setTypePlanList(falconData.planList.filter((item) => item.type === tableRow.id));
  };

  return (
    <ClickAwayListener onClickAway={editMode ? disregardChanges : () => null}>
      <TableRow
        key={`${tableRow.id}`}
        className={`${isCollapseRowActive ? classes.activeCollapseRow : classes.tableRow} ${isLastCollapseRow && classes.thickBorderCellBottom} ${editMode && classes.activeRow}`}
      >
        <TableCell className={`${classes.tableCell}`} colSpan={2}>
          <Box
            className={`${classes.tableCellContent}`}
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            <Typography>{tableRow.label}</Typography>
            {tableRow.editRow &&
            !editMode &&
            editCondition &&
            falconFilters.selectType === "week" ? (
              <CustomIconButton
                icon={<Edit />}
                iconColor={"darkGray"}
                theme={"gray"}
                action={() => setEditMode(true)}
              />
            ) : tableRow.editRow &&
              editMode &&
              editCondition &&
              falconFilters.selectType === "week" ? (
              <Box style={{ display: "flex", gap: 8 }}>
                <CustomIconButton
                  theme={"blue"}
                  icon={<Check />}
                  iconColor={"white"}
                  action={applyChanges}
                />
                <CustomIconButton
                  icon={<Close />}
                  iconColor={"white"}
                  theme={"red"}
                  action={disregardChanges}
                />
              </Box>
            ) : (
              ""
            )}
          </Box>
        </TableCell>
        <TableCell className={`${classes.tableCell}`} colSpan={2}>
          <Typography className={`${classes.tableCellContent}`}>{tableRow.sizeLabel}</Typography>
        </TableCell>
        <TableCell className={`${classes.tableCell} ${classes.borderCellRightDark}`} colSpan={2}>
          <Typography className={`${classes.tableCellContent}`}>
            {tableRow?.hour && tableRow.hour?.length <= 2 ? tableRow.hour : "n/a"}
          </Typography>
        </TableCell>
        {headColumns?.map((headColumn: DateLabelType) =>
          RenderFalconCell(
            classes,
            tableRow,
            falconData.standardList.filter((item: any) => item[dateType] === headColumn.date),
            falconData.planList.filter((item: any) => item[planDateType] === headColumn.date),
            editMode,
            typePlanList.filter((item: any) => item[planDateType] === headColumn.date),
            setTypePlanList,
            headColumn?.date,
          ),
        )}
        <TableCell className={`${classes.tableCell}`} colSpan={2}>
          <Typography className={`${classes.tableCellContent}`}>
            {getTotalScore(tableRow, falconData)}
          </Typography>
        </TableCell>
      </TableRow>
    </ClickAwayListener>
  );
};

const getTotalScore = (row: FalconTableRow, data: FalconDataLists) => {
  switch (row?.id) {
    case "planned":
      return data.planList?.reduce((a: number, b: FalconPlanData) => a + b.number_of_routes, 0);
    case "large-van":
      return data.planList
        ?.filter((item: FalconPlanData) => item?.type === "large-van")
        ?.reduce((a: number, b: FalconPlanData) => a + b.number_of_routes, 0);
    case "box-truck":
      return data.planList
        ?.filter((item: FalconPlanData) => item?.type === "box-truck")
        ?.reduce((a: number, b: FalconPlanData) => a + b.number_of_routes, 0);
    case "actual-billed":
      return data.standardList
        .filter(
          (item: FalconStandardData) =>
            ([0].includes(item.role) && [1, 2, 5, 6, null].includes(item.van__fleet_size)) ||
            ([8].includes(item.role) && [1, 2, 5, 6].includes(item.van__fleet_size)),
        )
        .reduce((a: number, b: FalconStandardData) => a + b.routes, 0);
    case "client-cancelled-cargo":
    case "client-cancelled-large":
    case "client-cancelled-medium":
    case "client-cancelled-cube":
    case "mycu-cancelled-cargo":
    case "mycu-cancelled-large":
    case "mycu-cancelled-cube":
    case "mycu-cancelled-medium":
      return data.standardList
        ?.filter(
          (item: FalconStandardData) =>
            row.role === item.role &&
            row?.vans?.includes(
              item?.van__fleet_size ? item?.van__fleet_size : item?.cancelled_fleet_size,
            ),
        )
        .reduce((a: number, b: FalconStandardData) => a + b.routes, 0);
    case "difference-routes":
      return "";
    default:
      return data.standardList
        .filter(
          (item: FalconStandardData) =>
            item?.role === row.role &&
            row?.vans?.includes(item?.van__fleet_size) &&
            row.hour?.includes(item?.role_hours),
        )
        .reduce((a: number, b: FalconStandardData) => a + b.routes, 0);
  }
};
